import { HTMLAttributes, Fragment } from 'react';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';

import { cn, formatMillionBalance } from '../utils/functions';

import { IconPlayerStatus } from './IconPlayerStatus';
import { TshirtTeam } from './TshirtTeam';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTournament } from '@/hooks/useTournament';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import {
  EnumTeamContext,
  PlayerClub,
  PlayerF5,
  PlayerHistoric,
} from '@/types/team';
import { PlayerMyTeamF5 } from '@/types/teamf5';

interface Props extends HTMLAttributes<HTMLButtonElement> {
  insured?: boolean;
  context?: EnumTeamContext;
  positionLabel?: string;
  data?: PlayerHistoric | PlayerClub | PlayerF5 | PlayerMyTeamF5;
  id: string;
  disabled?: boolean;
  displayDeleteIcon?: boolean;
  isSelected?: boolean;
}

const FieldPlayer = (props: Props) => {
  const {
    context,
    insured,
    className,
    onClick,
    data,
    positionLabel,
    id,
    disabled,
    displayDeleteIcon,
    isSelected = false,
    ...rest
  } = props;
  const player = usePlayerTemplateMap().get(data?.id);
  const name = player?.n;
  const lastName = player?.a;
  const statusId = player?.e;
  const formattedName = name?.trim().slice(0, 1);
  const { isDesktop } = useIsDesktop();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const team = player && teamsMap ? teamsMap.get(player?.c) : null;

  const { data: dataTshirtShield } = useTshirtAndShield();

  const hasCotization = (object: any): object is PlayerClub => {
    return 'cotizacion' in object;
  };

  const hasScore = (object: any): object is PlayerHistoric => {
    return 'puntaje' in object;
  };

  const isBuildTeam =
    context &&
    [EnumTeamContext.Build11, EnumTeamContext.Build5].includes(context);

  const isMyTeam =
    context &&
    (context === EnumTeamContext.Team11 || context === EnumTeamContext.Team5);

  const isScore =
    context &&
    (context === EnumTeamContext.Score11 || context === EnumTeamContext.Score5);

  return (
    <button
      className={cn(
        'fieldPlayer w-full flex flex-wrap cursor-pointer max-w-[3.375rem] min-w-[3.375rem] lg:max-w-[5.125rem] lg:min-w-[5.125rem] disabled:opacity-40 disabled:cursor-not-allowed',
        {
          'lg:max-w-[4.3rem] lg:min-w-[4.3rem]': !name,
          'z-[2]': isSelected,
        },
        className
      )}
      disabled={disabled}
      id={`refPlayerField-${id}`}
      type="button"
      onClick={disabled || isSelected ? undefined : onClick}
      {...rest}
    >
      <div className="m-auto relative w-full h-15 right-[.15rem]">
        {insured && name && (
          <IconPlayerStatus
            className="absolute left-0 top-0"
            variant="asegurado"
          />
        )}
        {isDesktop && context === EnumTeamContext.Team11 && (
          <div className="hidden lg:flex text-white bottom-[-10px] left-0 text-center absolute bg-[#18181887] min-w-[1.25rem] h-[1.25rem] items-center justify-center rounded-full">
            <FaArrowRightArrowLeft
              className="rotate-90 inline-block"
              size={10}
            />
          </div>
        )}
        <div
          className={cn(
            'min-w-[2.4em] lg:w-[2.4rem] w-[1.7rem] m-auto relative top-[.5rem] left-[.15rem]',
            { 'opacity-80': !name }
          )}
        >
          <TshirtTeam
            id={player?.id}
            shield={dataTshirtShield?.shield}
            tshirt={dataTshirtShield?.tshirt}
          />
        </div>
        {(!disabled || context === EnumTeamContext.Changes11) && (
          <Fragment>
            {name ? (
              <>
                {data?.capitan && (
                  <IconPlayerStatus
                    className="top-[5px] right-[-10px] absolute border-0"
                    variant="capitan"
                  />
                )}
                {data?.seguro && (
                  <IconPlayerStatus
                    className="top-[5px] left-[-5px] absolute border-0"
                    variant="asegurado"
                  />
                )}
                <IconPlayerStatus
                  className="bottom-[-10px] right-[-10px]"
                  forceIcon={displayDeleteIcon ? <FiX /> : undefined}
                  variant={statusId}
                />
              </>
            ) : (
              <IconPlayerStatus
                className="bottom-[-10px] right-[-10px]"
                forceIcon={displayDeleteIcon ? <FiX /> : undefined}
              />
            )}
          </Fragment>
        )}
      </div>
      <div
        className={cn(
          'bg-[#3C8335] pt-[6px] rounded-[0.25rem] lg:rounded-[.15rem] flex justify-center items-center w-full lg:h-[2.67rem] h-[2.4rem]',
          {
            'bg-[#3c833565] border-dashed border-[#E3E3E3] border-[.13rem] pb-[5px]':
              !name,
          }
        )}
      >
        {formattedName && lastName ? (
          <div className="w-full leading-none">
            <span
              className="flex items-center fieldPlayer__text--shadow justify-center lg:justify-center text-center text-white"
              style={{ lineHeight: 0 }}
            >
              <p className="leading-[1.2] lg:leading-[1.2] text-4xs lg:text-xs font-medium">
                {lastName.length > 11
                  ? `${lastName.slice(0, 11)}...`
                  : lastName}
              </p>
              {lastName.length < 10 && (
                <>
                  &nbsp;
                  <p className="leading-[1.2] lg:leading-[1.2] text-4xs lg:text-xs font-medium">
                    {formattedName}
                  </p>
                </>
              )}
            </span>
            {isBuildTeam && hasCotization(data) && (
              <span className="opacity-80 text-white text-4xs lg:text-2xs lg:leading-none block mt-[.05rem] lg:mt-1">
                $
                {data?.cotizacion
                  ? formatMillionBalance(data?.cotizacion)
                  : '-'}
              </span>
            )}
            {(isMyTeam || context === EnumTeamContext.Changes11) && (
              <span className="uppercase opacity-80 text-white text-5xs lg:text-[9px] lg:leading-none block mt-[.05rem] lg:mt-[.15rem]">
                {team?.nombre ? `${team?.nombre}` : '-'}
              </span>
            )}
            {isScore && hasScore(data) && (
              <span className="uppercase opacity-80 text-white text-5xs lg:text-[9px] lg:leading-none block mt-[.05rem] lg:mt-0">
                {data?.puntaje !== undefined ? `${data?.puntaje} PTS` : '-'}
              </span>
            )}
          </div>
        ) : (
          <p className="text-3xs lg:text-xs mt-1 lg:mt-0 font-semibold text-center text-white">
            {positionLabel}
          </p>
        )}
      </div>
      {data && isMyTeam && 'titular' in data && !data.titular && (
        <p className="text-center min-w-full text-white mt-2">
          {positionLabel}
        </p>
      )}
    </button>
  );
};

export default FieldPlayer;
